<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import ButtonV4 from 'next/button/Button.vue';
import AccountAPI from 'dashboard/api/account';

const { t } = useI18n();

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['success', 'close']);

const isLoading = ref(false);
const isStripeLoaded = ref(false);
const stripe = ref(null);
const cardElement = ref(null);
const cardErrors = ref('');
const isFormValid = ref(false);

const stripePublishableKey = computed(() => {
  // Get from window config or environment
  return window.chatwootConfig?.stripePublishableKey || '';
});

const loadStripe = async () => {
  try {
    if (!stripePublishableKey.value) {
      useAlert(t('BILLING_SETTINGS.PAYMENT_METHOD.STRIPE_LOAD_ERROR'));
      return;
    }

    if (window.Stripe) {
      stripe.value = window.Stripe(stripePublishableKey.value);
      isStripeLoaded.value = true;
      setupCardElement();
      return;
    }

    // Load Stripe.js if not already loaded
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      stripe.value = window.Stripe(stripePublishableKey.value);
      isStripeLoaded.value = true;
      setupCardElement();
    };
    document.head.appendChild(script);
  } catch (error) {
    console.error('Failed to load Stripe:', error);
    useAlert(t('BILLING_SETTINGS.PAYMENT_METHOD.STRIPE_LOAD_ERROR'));
  }
};

const setupCardElement = () => {
  if (!stripe.value) return;

  if (cardElement.value) {
    cardElement.value.unmount();
    cardElement.value = null;
  }

  const elements = stripe.value.elements();
  cardElement.value = elements.create('card', {
    style: {
      base: {
        fontSize: '14px',
        color: '#1f2937',
        fontFamily: 'Inter, system-ui, sans-serif',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#ef4444',
      },
    },
  });

  cardElement.value.mount('#card-element');

  cardElement.value.on('change', event => {
    cardErrors.value = event.error ? event.error.message : '';
    isFormValid.value = event.complete;
  });
};

const handleSubmit = async () => {
  if (!stripe.value || !cardElement.value || !isFormValid.value) {
    return;
  }

  isLoading.value = true;
  cardErrors.value = '';

  try {
    // Create payment method with Stripe
    const { error, paymentMethod } = await stripe.value.createPaymentMethod({
      type: 'card',
      card: cardElement.value,
    });

    if (error) {
      cardErrors.value = error.message;
      isLoading.value = false;
      return;
    }

    // Send payment method to backend to attach to Lago customer
    await AccountAPI.addPaymentMethod(props.accountId, {
      payment_method_id: paymentMethod.id,
    });

    useAlert(t('BILLING_SETTINGS.PAYMENT_METHOD.SUCCESS'));
    emit('success');
  } catch (error) {
    console.error('Payment method error:', error);
    cardErrors.value =
      error.response?.data?.error || t('BILLING_SETTINGS.PAYMENT_METHOD.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const close = () => {
  emit('close');
};

onBeforeUnmount(() => {
  if (cardElement.value) {
    cardElement.value.unmount();
    cardElement.value = null;
  }
});

onMounted(() => {
  loadStripe();
});
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
      >
        <h3 class="text-lg font-semibold text-n-strongest">
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.TITLE') }}
        </h3>
        <button
          class="text-n-light hover:text-n-strongest transition-colors"
          @click="close"
        >
          <i class="i-lucide-x text-xl" />
        </button>
      </div>

      <!-- Body -->
      <div class="px-6 py-4">
        <p class="text-sm text-n-light mb-4">
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.DESCRIPTION') }}
        </p>

        <!-- Stripe Card Element -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-n-strong mb-2">
            {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.CARD_DETAILS') }}
          </label>
          <div
            id="card-element"
            class="px-3 py-2 border border-n-weak rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
          />
          <p v-if="cardErrors" class="mt-2 text-sm text-red-600">
            {{ cardErrors }}
          </p>
        </div>

        <!-- Security Notice -->
        <div
          class="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4"
        >
          <i class="i-lucide-shield-check text-blue-600 text-lg mt-0.5" />
          <p class="text-xs text-blue-900">
            {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.SECURITY_NOTE') }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
      >
        <ButtonV4 sm outline @click="close">
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.CANCEL') }}
        </ButtonV4>
        <ButtonV4
          sm
          solid
          blue
          :loading="isLoading"
          :disabled="!isFormValid || isLoading || !isStripeLoaded"
          @click="handleSubmit"
        >
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.ADD_CARD') }}
        </ButtonV4>
      </div>
    </div>
  </div>
</template>
